import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./CheckoutPage.css";
import "../components/PaymentMethodStyles.css";
import { useUser } from "../../../../context/UserContext";
import { useLanguage } from "../../../../context/LanguageContext";
import { clearCart } from "../../../../firebase/cart-functions";
import { processCheckout } from "../../../../firebase/checkout-functions";
import { createCheckoutSession } from "../../../../firebase/stripe-checkout-functions";
import { getLocalizedProductName } from "../../../../utils/multilingualUtils";
import { toast } from "react-toastify";
import { DEFAULT_STORE_ID } from "../../../../firebase/firebase";

// Import custom hooks
import { useCheckoutData } from "../../../../hooks/useCheckoutData";
import { useOrderCalculations } from "../../../../hooks/useOrderCalculations";
import { useCartValidation } from "../../../../hooks/useCartValidation";
import { useDynamicPageTitle } from "../../../../hooks/useDynamicPageTitle";

// Import components
import ShippingMethodSection from "../components/ShippingMethodSection";
import PaymentMethodSection from "../components/PaymentMethodSection";
import OrderNotesSection from "../components/OrderNotesSection";
import OrderSummary from "../components/OrderSummary";
import CustomerInfoForm from "../../../../components/common/CustomerInfoForm/CustomerInfoForm";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const { language } = useLanguage();
  // const { stripePromise } = useStripeContext();

  // Set dynamic page title using store settings
  useDynamicPageTitle('checkout');

  // Translations for checkout page
  const translations = {
    en: {
      checkout: "Checkout",
      loadingCheckout: "Loading checkout...",
      cartEmpty: "Your cart is empty",
      addItemsMessage: "Add some items to your cart before proceeding to checkout.",
      continueShopping: "Continue Shopping",
      fillRequiredInfo: "Please fill in all required billing information",
      selectShippingMethod: "Please select a shipping method",
      insufficientStock: "Insufficient stock for: {items}. Please adjust quantities and try again.",
    },
    ar: {
      checkout: "إتمام الطلب",
      loadingCheckout: "جاري تحميل صفحة الطلب...",
      cartEmpty: "سلة التسوق فارغة",
      addItemsMessage: "أضف بعض المنتجات إلى سلة التسوق قبل المتابعة إلى إتمام الطلب.",
      continueShopping: "متابعة التسوق",
      fillRequiredInfo: "يرجى ملء جميع معلومات الفوترة المطلوبة",
      selectShippingMethod: "يرجى اختيار طريقة الشحن",
      insufficientStock: "مخزون غير كافٍ لـ: {items}. يرجى تعديل الكميات والمحاولة مرة أخرى.",
    }
  };

  const t = translations[language] || translations.en;

  // Load initial data
  const {
    cart,
    storeSettings,
    loading,
    error: dataError,
    setCart,
  } = useCheckoutData(currentUser);

  // Validate cart items before checkout
  const { validateCartItems, removeUnavailableItems, isValidating } =
    useCartValidation();

  // Calculate order totals with promo code support
  const {
    summary,
    discountAmount,
    appliedPromo,
    promoLoading,
    promoError,
    validatedItems,
    calculateSummary,
    applyDiscount,
    removeDiscount,
  } = useOrderCalculations(cart, storeSettings, currentUser);

  // Form states
  const [formData, setFormData] = useState({
    billingInfo: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      country: "UAE",
    },
    shippingInfo: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      country: "UAE",
    },
    createAccount: false,
    accountPassword: "",
    confirmPassword: "",
    paymentMethod: "", // No default payment method
    paymentReceipt: null, // For manual transfer receipt upload
    shippingMethod: "",
    sameAsBilling: true,
    orderNotes: "",
    discountCode: "",
  });

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [isCalculating, setIsCalculating] = useState(false); // New state for server calculations

  // Calculate initial summary when cart or store settings change
  useEffect(() => {
    const calculateInitialSummary = async () => {
      if (cart && storeSettings) {
        // If shipping is not required, set a dummy shipping method to allow calculations
        const requiresShipping = storeSettings.requiresShipping !== false;
        const shippingMethodId = requiresShipping ? formData.shippingMethod : null;

        await calculateSummary(cart, shippingMethodId);
      }
    };
    calculateInitialSummary();
  }, [cart?.items?.length, storeSettings?.storeId, storeSettings?.requiresShipping, formData.shippingMethod]); // Remove calculateSummary from deps

  // Handle shipping method change and recalculate
  const handleShippingMethodChange = async (shippingMethodId) => {
    setFormData((prev) => ({
      ...prev,
      shippingMethod: shippingMethodId,
    }));

    // Show loading state during calculation
    setIsCalculating(true);

    try {
      // Recalculate summary with new shipping method
      if (cart && storeSettings) {
        await calculateSummary(cart, shippingMethodId);
      }
    } finally {
      setIsCalculating(false);
    }
  };

  // Enhanced promo code application
  const handleApplyPromoCode = async (promoCode) => {
    if (!cart || !cart.items || cart.items.length === 0) {
      toast.error("Cannot apply promo code to empty cart");
      return false;
    }

    setIsCalculating(true); // Show loading state

    try {
      const success = await applyDiscount(promoCode, formData.shippingMethod);
      // Note: calculateSummary is now called within applyDiscount
      return success;
    } finally {
      setIsCalculating(false);
    }
  };

  // Enhanced promo code removal
  const handleRemovePromoCode = () => {
    setIsCalculating(true); // Show loading state

    try {
      removeDiscount(formData.shippingMethod);
      // Note: calculateSummary is now called within removeDiscount
    } finally {
      setIsCalculating(false);
    }
  };

  // Handle form submission with improved error handling
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (processing || isValidating) return;

    // Check if shipping is required for this store
    const requiresShipping = storeSettings?.requiresShipping !== false; // Default to true for backward compatibility

    // Basic form validation - always require personal info
    if (
      !formData.billingInfo.firstName ||
      !formData.billingInfo.lastName ||
      !formData.billingInfo.email ||
      !formData.billingInfo.phone
    ) {
      setError(t.fillRequiredInfo);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Only validate shipping if required
    if (requiresShipping && !formData.shippingMethod) {
      setError(t.selectShippingMethod);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setProcessing(true);
    setError("");

    try {
      // Step 1: Validate cart items BEFORE processing

      const validation = await validateCartItems(cart.items, DEFAULT_STORE_ID);

      if (!validation.isValid) {
        // Handle validation failures
        if (validation.unavailableItems.length > 0) {
          const unavailableNames = validation.unavailableItems
            .map((item) => getLocalizedProductName(item, language))
            .join(", ");
          const message = `The following items are no longer available: ${unavailableNames}. They will be removed from your cart.`;

          toast.warning(message);

          // Remove unavailable items from cart
          const updatedCart = await removeUnavailableItems(
            cart,
            validation.unavailableItems,
            currentUser
          );
          setCart(updatedCart);

          // Recalculate summary with updated cart
          await calculateSummary(updatedCart, formData.shippingMethod);

          setProcessing(false);
          return;
        }

        if (validation.insufficientStockItems.length > 0) {
          const stockIssues = validation.insufficientStockItems
            .map(
              (item) =>
                `${getLocalizedProductName(item, language)} (requested: ${item.requestedQuantity}, available: ${item.availableQuantity})`
            )
            .join(", ");

          setError(
            t.insufficientStock.replace("{items}", stockIssues)
          );
          setProcessing(false);
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
      }

      // Step 2: Proceed with checkout (promo validation already done in useOrderCalculations)


      // Get selected shipping option details
      const selectedShipping =
        storeSettings.shippingOptions.find(
          (option) => option.id === formData.shippingMethod
        ) || {};

      // Customer data
      const customerData = {
        firstName: formData.billingInfo.firstName,
        lastName: formData.billingInfo.lastName,
        email: formData.billingInfo.email,
        phone: formData.billingInfo.phone,
        address: formData.billingInfo.address,
        city: formData.billingInfo.city,
        state: formData.billingInfo.state,
        zipCode: formData.billingInfo.zipCode,
        country: formData.billingInfo.country,
      };

      // Prepare order data - Send only essential data, let server calculate all prices
      const orderData = {
        items: cart.items.map((item) => ({
          productId: item.productId,
          variantId: item.variantId || null,
          quantity: item.quantity || 1,
          // Keep display data for order records (non-pricing)
          displayVariant: item.displayVariant || null,
          ...(item.style && { style: item.style }),
          ...(item.type && { type: item.type }),
        })),
        // Remove client-side totals - server will recalculate everything
        shipping: {
          method: selectedShipping.name || "Standard Shipping",
          methodId: formData.shippingMethod,
          cost: selectedShipping.price || 0,
        },
        // Include promo code for server-side validation and calculation
        promoCode: appliedPromo ? { code: appliedPromo.code } : null,
        currency: storeSettings.currency || "",
        billingInfo: formData.billingInfo,
        shippingInfo: formData.sameAsBilling
          ? formData.billingInfo
          : formData.shippingInfo,
        customerData: customerData, // Include customer data in order data
        paymentMethod: formData.paymentMethod,
        paymentReceipt: formData.paymentReceipt || null, // Include receipt URL for manual transfer
        orderNotes: formData.orderNotes || "",
        userId: currentUser?.uid || null,
        status: formData.paymentMethod === "manual_transfer" ? "pending" : "pending", // Set pending for manual transfer
        storeId: DEFAULT_STORE_ID,
        createdAt: new Date().toISOString(),
      };
      JSON.stringify(orderData, (key, value) => {
        // console.error(`❌ Found undefined value at key: ${key}`);
        if (value === undefined) {
        }
        return value;
      });

      // Route to appropriate payment method
      // if (formData.paymentMethod === 'credit_card') {
      //   // STRIPE EMBEDDED PAYMENT FLOW
      //   await handleStripePayment(orderData, customerData);
      // } else 
      if (formData.paymentMethod === 'hosted_checkout') {
        // STRIPE HOSTED CHECKOUT FLOW
        await handleHostedCheckout(orderData, customerData);
      } else {
        // CASH ON DELIVERY FLOW (existing)
        await handleCashOnDeliveryPayment(orderData, customerData);
      }

    } catch (err) {
      console.error("Checkout error:", err);
      setError(err.message || "An error occurred during checkout");
      setProcessing(false);
    }
  };

  // Handle Hosted Checkout flow (with Apple Pay support)
  const handleHostedCheckout = async (orderData, customerData) => {
    try {


      // Create checkout session and redirect to Stripe
      const result = await createCheckoutSession(
        orderData,
        customerData,
        orderData.shipping.methodId,
        appliedPromo?.code || null,
        orderData.shipping.cost
      );

      if (result && result.success && result.sessionData) {


        // Store order data in localStorage for post-payment processing
        localStorage.setItem('pendingHostedOrder', JSON.stringify({
          sessionId: result.sessionData.sessionId,
          orderData: orderData,
          customerData: customerData,
          timestamp: new Date().toISOString()
        }));

        // Redirect to Stripe hosted checkout
        window.location.href = result.sessionData.sessionUrl;
      } else {
        throw new Error("Failed to create checkout session");
      }
    } catch (error) {
      console.error("Hosted checkout initialization error:", error);
      setError(error.message || "Failed to initialize payment. Please try again.");
      setProcessing(false);
      toast.error(error.message || "Payment initialization failed. Please try again.");
    }
  };

  // Handle cash on delivery payment (existing logic)
  const handleCashOnDeliveryPayment = async (orderData, customerData) => {
    try {
      // Process the checkout
      const result = await processCheckout(
        orderData,
        customerData,
        currentUser,
        formData.createAccount && !currentUser,
        formData.createAccount && !currentUser ? {
          displayName: `${customerData.firstName} ${customerData.lastName}`,
          phoneNumber: customerData.phone,
          firstName: customerData.firstName,
          lastName: customerData.lastName
        } : null
      );

      if (result.success) {

        // Success handling
        localStorage.setItem(
          "lastOrderInfo",
          JSON.stringify({
            orderId: result.orderId,
            storeOrderId: result.storeOrderId,
            storeId: DEFAULT_STORE_ID,
            timestamp: new Date().toISOString(),
          })
        );

        // Clear the cart
        try {
          await clearCart(currentUser);
        } catch (clearError) {

          localStorage.removeItem("guestCart");
        }

        navigate(`/order-confirmation/${result.orderId}`);
      } else {
        throw new Error(result.message || "Order processing failed");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setError(
        err.message || "An error occurred during checkout. Please try again."
      );
      toast.error(err.message || "Checkout failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="checkout-loading">
        <div className="loader"></div>
        <p>{t.loadingCheckout}</p>
      </div>
    );
  }

  // Empty cart state
  if (!loading && (!cart || !cart.items || cart.items.length === 0)) {
    return (
      <div className="empty-checkout">
        <h2>{t.cartEmpty}</h2>
        <p>{t.addItemsMessage}</p>
        <button onClick={() => navigate("/shop")} className="continue-shopping">
          {t.continueShopping}
        </button>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <h1>{t.checkout}</h1>
      </div>

      {(error || dataError) && (
        <div className="checkout-error">{error || dataError}</div>
      )}

      <form onSubmit={handleSubmit} className="checkout-form">
        <div className="checkout-wrapper">
          <div className="checkout-left">
            <CustomerInfoForm
              formData={formData}
              setFormData={setFormData}
              storeSettings={storeSettings}
              processing={processing}
              showCreateAccount={true}
              showUseExistingInfo={!!currentUser}
              showShippingToggle={storeSettings?.requiresShipping !== false} // Only show shipping toggle if shipping is required
              mode="checkout"
            />

            {/* Only show shipping method section if shipping is required */}
            {storeSettings?.requiresShipping !== false && (
              <ShippingMethodSection
                formData={formData}
                setFormData={setFormData}
                storeSettings={storeSettings}
                onShippingChange={handleShippingMethodChange}
                cart={cart}
                processing={processing}
              />
            )}

            <PaymentMethodSection
              formData={formData}
              setFormData={setFormData}
              storeSettings={storeSettings}
              processing={processing}
            />

            <OrderNotesSection
              formData={formData}
              setFormData={setFormData}
              processing={processing}
            />
          </div>

          <div className="checkout-right">
            <OrderSummary
              cart={cart}
              validatedItems={validatedItems}
              summary={summary}
              storeSettings={storeSettings}
              discountAmount={discountAmount}
              appliedPromo={appliedPromo}
              promoLoading={promoLoading}
              promoError={promoError}
              formData={formData}
              setFormData={setFormData}
              onApplyDiscount={handleApplyPromoCode}
              onRemoveDiscount={handleRemovePromoCode}
              processing={processing}
              isValidating={isValidating}
              navigate={navigate}
              isCalculating={isCalculating}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default CheckoutPage;